import type {
	CreateExclusionGroupRequest,
	ExclusionGroupDoc,
	UpdateExclusionGroupRequest,
} from '@orderlymeds/measurements-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

export const exclusionGroupKeys = {
	all: (projectId: string) => ['projects', projectId, 'exclusion-groups'] as const,
	detail: (projectId: string, groupId: string) =>
		['projects', projectId, 'exclusion-groups', groupId] as const,
};

export function useExclusionGroups(projectId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: exclusionGroupKeys.all(projectId),
		queryFn: () =>
			apiFetch<ExclusionGroupDoc[]>(`/api/admin/projects/${projectId}/exclusion-groups`),
		enabled: !!projectId,
		staleTime: 30_000,
	});
}

export function useExclusionGroup(projectId: string, groupId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: exclusionGroupKeys.detail(projectId, groupId),
		queryFn: () =>
			apiFetch<ExclusionGroupDoc>(`/api/admin/projects/${projectId}/exclusion-groups/${groupId}`),
		enabled: !!projectId && !!groupId,
		staleTime: 30_000,
	});
}

export function useCreateExclusionGroup(projectId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateExclusionGroupRequest) =>
			apiFetch<ExclusionGroupDoc>(`/api/admin/projects/${projectId}/exclusion-groups`, {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: exclusionGroupKeys.all(projectId) });
		},
	});
}

export function useUpdateExclusionGroup(projectId: string, groupId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateExclusionGroupRequest) =>
			apiFetch<ExclusionGroupDoc>(`/api/admin/projects/${projectId}/exclusion-groups/${groupId}`, {
				method: 'PUT',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: exclusionGroupKeys.all(projectId) });
			queryClient.invalidateQueries({
				queryKey: exclusionGroupKeys.detail(projectId, groupId),
			});
		},
	});
}

export function useArchiveExclusionGroup(projectId: string, groupId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () =>
			apiFetch<void>(`/api/admin/projects/${projectId}/exclusion-groups/${groupId}`, {
				method: 'DELETE',
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: exclusionGroupKeys.all(projectId) });
		},
	});
}
